import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntersection } from '@dm/intersection-observer';
import { useFela } from '../..';
import { WithDmId } from '../../utils/BaseProps';

interface PictureStyles {
  picture?: any;
  image?: any;
}

export type PictureProps = WithDmId & {
  /**
   * String, für den Alternativtext des Bildes.
   */
  alt: string;
  /**
   * String, um ein Platzhalterbild anzuzeigen, solange das Bild noch nicht geladen wurde.
   */
  placeholderSrc?: string;
  /**
   * Boolean, mit dem eingestellt werden kann, ob das Bild vorgeladen werden soll.
   */
  preFetch?: boolean;
  /**
   * Objekt, in dem mehrere Bildquellen mit mediaQuery übergeben werden können.
   * In diesem Fall wird preFetch ignoriert, da die jeweilige Bildquelle vom Browser gewählt wird.
   */
  sources?: Array<{ src: string; media: string }>;
  /**
   * String, der die Bildquelle bestimmt.
   */
  src: string;
  /**
   * Objekt, um das Styling getrennt für image und picture anzupassen.
   */
  styles?: PictureStyles;
};

/**
 *
 * This Component renders an image
 * It takes control of lazy loading, responsive and prefetching
 */
const Picture = ({
  src,
  alt,
  sources,
  preFetch,
  placeholderSrc,
  dmId = 'dm-picture',
  styles = {},
  ...other
}: PictureProps) => {
  const [isImagePreFetched, setIsImagePreFetched] = useState(false);
  const imageRef = useRef(null);
  const isIntersecting = useIntersection(imageRef, { rootMargin: '500px' });
  const { css } = useFela();

  useEffect(() => {
    // Image should be prefetched on intersection
    // By using sources (with the picture tag), the browser controls the image size,
    // so we cannot prefetch a specific image size here
    const shouldImageBePreFetched = !sources && (isIntersecting || preFetch);
    if (shouldImageBePreFetched) {
      const image = new Image();
      image.src = src;
      image.onload = () => {
        setIsImagePreFetched(true);
      };
    }
  }, [isIntersecting, preFetch, sources, src]);

  return sources ? (
    <picture
      data-designsystem
      ref={imageRef}
      data-dmid={dmId}
      className={css(styles.picture)}>
      {sources.map(source => (
        <source
          srcSet={isIntersecting ? source.src : placeholderSrc}
          media={`(${source.media})`}
        />
      ))}
      <img
        alt={alt}
        src={isIntersecting ? src : placeholderSrc}
        data-src={src}
        className={css(styles.image)}
        {...other}
      />
    </picture>
  ) : (
    <img
      data-designsystem
      data-dmid={dmId}
      ref={imageRef}
      alt={alt}
      src={isImagePreFetched ? src : placeholderSrc}
      data-src={src}
      className={css(styles.image)}
      {...other}
    />
  );
};

Picture.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  dmId: PropTypes.string,
  placeholderSrc: PropTypes.string,
  preFetch: PropTypes.bool,
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      media: PropTypes.string.isRequired,
    }),
  ),
  styles: PropTypes.shape({
    picture: PropTypes.any,
    image: PropTypes.any,
  }),
};

export default Picture;
